import { produce } from 'immer';
import { useEffect, useReducer } from 'react';
import {
    deleteMedicationListEntry,
    getMedicationList,
    searchMedicationListEntries,
    updateMedicationListEntry
} from '../../Async/MedicationListsAsync';
import { AutosaveStates } from '../../Components/AutosaveIndicator';
import { useError } from '../../Components/ErrorBoundary';
import { displayMessage } from '../../Config/ApplicationConfig';
import { EditMedicationListEntry } from '../../Contracts/EditMedicationListEntry.Request';
import { MedicationListResponse } from '../../Contracts/MedicationList.Response';
import { MedicationListEntriesSearchFilter } from '../../Contracts/MedicationListEntriesSearchFilter.Request';
import { MedicationListEntriesSearchResult } from '../../Contracts/MedicationListEntriesSearchResult.Response';
import { ListBulkAction } from '../../Contracts/MedicationListModels';
import { MedicationListStatus } from '../../Contracts/MedicationListsSearchFilter.Request';
import { useDebounce } from '../../Hooks/useDebounce';
import { CancelledRequest, useHttpConfig } from '../../Shared/HttpRequestExecutor';
import { Nullable } from '../../Shared/Types';

export enum EntryLoadingState {
    Init,
    Loading,
    Loaded,
    Refreshing
}

export enum MedListLoadingState {
    Init,
    Loading,
    Loaded
}

export type MedListState = {
    medicationListLoadingState: MedListLoadingState;
    medicationList: Nullable<MedicationListResponse>;
    entries: MedicationListEntriesSearchResult[];
    queryUsed: string;
    entryLoadingState: EntryLoadingState;
    query: string;
    takeBulkAction: Nullable<ListBulkAction>;
    editRowId: string;
    updatingRow: boolean;
    deleteRow: Nullable<MedicationListEntriesSearchResult>;
    deletingRow: boolean;
};

export type Actions =
    | {
          type: 'QueryUpdate';
          query: string;
      }
    | {
          type: 'MedicationListLoading';
      }
    | {
          type: 'MedicationListLoaded';
          medicationList: MedicationListResponse;
      }
    | {
          type: 'MedicationListEntriesLoading';
      }
    | {
          type: 'MedicationListEntriesLoaded';
          medicationListEntries: MedicationListEntriesSearchResult[];
          queryUsed: string;
      }
    | {
          type: 'BulkActionTrigger';
          action: ListBulkAction;
      }
    | {
          type: 'BulkActionCancel';
      }
    | {
          type: 'BulkActionSuccess';
      }
    | {
          type: 'PublishedToggle';
          published: boolean;
      }
    | {
          type: 'RowSavingComplete';
          row: MedicationListEntriesSearchResult;
      }
    | {
          type: 'RowEditStart';
          rowId: string;
      }
    | {
          type: 'RowEditCancel';
      }
    | {
          type: 'RowSavingStarted';
      }
    | {
          type: 'DeleteEntryStart';
          row: MedicationListEntriesSearchResult;
      }
    | {
          type: 'DeleteEntryStarting';
      }
    | {
          type: 'DeleteEntryCancel';
      }
    | {
          type: 'DeleteEntryComplete';
      };

const reducer = produce((state: MedListState, action: Actions) => {
    switch (action.type) {
        case 'BulkActionCancel':
            state.takeBulkAction = null;
            break;
        case 'QueryUpdate':
            state.query = action.query;
            break;
        case 'MedicationListLoading':
            state.medicationListLoadingState = MedListLoadingState.Loading;
            break;
        case 'MedicationListLoaded':
            state.medicationList = action.medicationList;
            state.medicationListLoadingState = MedListLoadingState.Loaded;
            break;
        case 'MedicationListEntriesLoading':
            if (state.entryLoadingState == EntryLoadingState.Loaded)
                state.entryLoadingState = EntryLoadingState.Refreshing;
            else state.entryLoadingState = EntryLoadingState.Loading;
            break;
        case 'MedicationListEntriesLoaded':
            state.entries = action.medicationListEntries;
            state.entryLoadingState = EntryLoadingState.Loaded;
            state.queryUsed = action.queryUsed;
            break;
        case 'BulkActionTrigger':
            state.takeBulkAction = action.action;
            break;
        case 'BulkActionSuccess':
            state.query = '';
            state.takeBulkAction = null;
            break;
        case 'PublishedToggle':
            if (state.medicationList)
                state.medicationList.status = action.published
                    ? MedicationListStatus.Published
                    : MedicationListStatus.Draft;
            break;
        case 'RowSavingComplete':
            state.editRowId = '';
            state.updatingRow = false;
            state.entries = state.entries.map(it => {
                if (it.id === action.row.id) return action.row;

                return it;
            });
            break;
        case 'RowEditStart':
            state.editRowId = action.rowId;
            break;
        case 'RowEditCancel':
            state.editRowId = '';
            break;
        case 'RowSavingStarted':
            state.updatingRow = true;
            break;
        case 'DeleteEntryStart':
            state.deleteRow = action.row;
            break;
        case 'DeleteEntryStarting':
            state.deletingRow = true;
            break;
        case 'DeleteEntryCancel':
            state.deleteRow = null;
            break;
        case 'DeleteEntryComplete':
            state.deletingRow = false;
            if (state.deleteRow) {
                state.entries = state.entries.filter(it => it.id !== state.deleteRow?.id);
                state.deleteRow = null;
            }
            break;
    }
});

export function useMedicationListState(props: { medListId: string; onDelete(): void }) {
    const throwError = useError();
    const [state, dispatch] = useReducer(reducer, {
        medicationListLoadingState: MedListLoadingState.Init,
        medicationList: null,
        entries: [],
        entryLoadingState: EntryLoadingState.Init,
        query: '',
        queryUsed: '',
        takeBulkAction: null,
        editRowId: '',
        updatingRow: false,
        deleteRow: null,
        deletingRow: false
    });

    const getConfig = useHttpConfig();
    const loadMedicationList = async () => {
        dispatch({
            type: 'MedicationListLoading'
        });

        try {
            const medList = await getMedicationList(getConfig, props.medListId);
            if (medList instanceof CancelledRequest) return;

            dispatch({
                type: 'MedicationListLoaded',
                medicationList: medList
            });
        } catch (e) {
            throwError(e);
        }
    };

    const listConfig = useHttpConfig();
    const loadEntries = async (query: string) => {
        dispatch({
            type: 'MedicationListEntriesLoading'
        });

        try {
            const filter: MedicationListEntriesSearchFilter = {};
            const entries = await searchMedicationListEntries(listConfig, props.medListId, query, 101, filter);
            if (entries instanceof CancelledRequest) return;

            dispatch({
                type: 'MedicationListEntriesLoaded',
                medicationListEntries: entries,
                queryUsed: query
            });
        } catch (e) {
            throwError(e);
        }
    };

    useEffect(() => {
        loadMedicationList();
    }, []);

    const debounceQuery = useDebounce(state.query);

    useEffect(() => {
        loadEntries(debounceQuery);
    }, [debounceQuery]);

    const refreshEntries = () => {
        loadEntries(debounceQuery);
    };

    const reloadMedicationList = () => {
        loadMedicationList();
        loadEntries(debounceQuery);
    };

    const handleSaveMedication = async (newData: MedicationListEntriesSearchResult) => {
        try {
            const payload: EditMedicationListEntry = {
                ...newData
            };

            await updateMedicationListEntry(props.medListId, newData.id, payload);

            dispatch({ type: 'RowSavingComplete', row: newData });
        } catch (e) {
            throwError(e);
        }
    };

    const handleDeleteMedication = async () => {
        dispatch({ type: 'DeleteEntryStarting' });
        try {
            if (state.deleteRow) await deleteMedicationListEntry(props.medListId, state.deleteRow.id);
            dispatch({ type: 'DeleteEntryComplete' });
            refreshEntries();
        } catch (e) {
            throwError(e);
        }
    };

    const showDeleteMedicationDialog = (rowData: MedicationListEntriesSearchResult) => {
        dispatch({ type: 'DeleteEntryStart', row: rowData });
    };

    const cancelDeleteMedication = () => {
        dispatch({ type: 'DeleteEntryCancel' });
    };

    const handlePublishedToggled = (published: boolean) => {
        dispatch({ type: 'PublishedToggle', published });
    };

    const startRowEditing = (row: MedicationListEntriesSearchResult) => {
        dispatch({ type: 'RowEditStart', rowId: row.id });
    };

    const cancelRowEditing = () => {
        dispatch({ type: 'RowEditCancel' });
    };

    const updateQuery = (query: string) => {
        dispatch({ type: 'QueryUpdate', query });
    };

    const showBulkActionWizard = (action: ListBulkAction) => {
        dispatch({ type: 'BulkActionTrigger', action });
    };

    const cancelBulkAction = () => {
        dispatch({ type: 'BulkActionCancel' });
    };

    const bulkActionComplete = (result: { itemsAdded: number; itemsRemoved: number }) => {
        if (!!result.itemsAdded || !!result.itemsRemoved) {
            const message = [
                result.itemsAdded > 0 ? `${result.itemsAdded} items added to list.` : '',
                result.itemsRemoved > 0 ? `${result.itemsRemoved} items removed from list.` : ''
            ]
                .filter(it => !!it)
                .join(' ');

            displayMessage(message);
        } else {
            displayMessage('No items effected.');
        }
        dispatch({ type: 'BulkActionSuccess' });
        refreshEntries();
    };

    const autoSaveStatus: AutosaveStates = state.updatingRow ? AutosaveStates.saving : AutosaveStates.saved;

    return {
        state,
        autoSaveStatus,
        updateQuery,
        startRowEditing,
        cancelRowEditing,
        cancelBulkAction,
        reloadMedicationList,
        handleSaveMedication,
        showBulkActionWizard,
        refreshEntries,
        bulkActionComplete,
        handleDeleteMedication,
        handlePublishedToggled,
        cancelDeleteMedication,
        showDeleteMedicationDialog
    };
}
